/**
 * swissALTI3D (swisstopo) — 0.5 m / 2 m over Switzerland, the highest-quality
 * tier available to this app.
 *
 * Assets are cloud-optimised GeoTIFFs in EPSG:2056 (LV95), discovered through
 * the STAC API. geotiff.js reads them over HTTP range requests, so only the
 * pixels inside the requested window are transferred rather than whole tiles.
 *
 * The 2 m product is used rather than 0.5 m: our working cell size is 10–60 m,
 * so 0.5 m would be sixteen times the traffic for detail the solver immediately
 * averages away.
 */

import { fromUrl, type GeoTIFF } from 'geotiff';
import type { DemProvider, DemRequest, HeightGrid } from '../types';
import { NoCoverageError, REGIONS } from '../types';
import { gridToLonLat, type MetricDomain } from '../projection';

const STAC =
  'https://data.geo.admin.ch/api/stac/v0.9/collections/ch.swisstopo.swissalti3d/items';

/** swissALTI3D assets are 1 km tiles, so a wide view would need hundreds. */
const MAX_TILES = 36;
const MAX_DOMAIN_M = 6500;

/**
 * WGS84 → LV95, swisstopo's published approximation. Accurate to well under a
 * metre inside Switzerland, which is far finer than the 2 m source data.
 */
export function wgs84ToLv95(lon: number, lat: number): [number, number] {
  const phi = (lat * 3600 - 169028.66) / 10000;
  const lam = (lon * 3600 - 26782.5) / 10000;
  const phi2 = phi * phi;
  const lam2 = lam * lam;

  const E =
    2600072.37 +
    211455.93 * lam -
    10938.51 * lam * phi -
    0.36 * lam * phi2 -
    44.54 * lam2 * lam;
  const N =
    1200147.07 +
    308807.95 * phi +
    3745.25 * lam2 +
    76.63 * phi2 -
    194.56 * lam2 * phi +
    119.79 * phi2 * phi;
  return [E, N];
}

interface Tile {
  raster: Float32Array;
  width: number;
  height: number;
  /** LV95 bounds: [minE, minN, maxE, maxN] */
  bbox: [number, number, number, number];
}

export const swisstopoProvider: DemProvider = {
  info: {
    id: 'swisstopo',
    label: 'swissALTI3D',
    nativeResolution: 2,
    attribution: 'Elevation: swissALTI3D © swisstopo (CC-BY)',
    covers: REGIONS.switzerland,
  },

  async load(req: DemRequest, domain: MetricDomain): Promise<HeightGrid> {
    if (domain.size > MAX_DOMAIN_M) {
      // Too wide for 1 km tiles; the global tier handles regional views.
      throw new NoCoverageError('swisstopo (area too large)');
    }

    const b = domain.bbox;
    const url = `${STAC}?bbox=${b[0].toFixed(5)},${b[1].toFixed(5)},${b[2].toFixed(5)},${b[3].toFixed(5)}&limit=100`;
    const res = await fetch(url, { signal: req.signal });
    if (!res.ok) throw new NoCoverageError('swisstopo');
    const json = (await res.json()) as {
      features?: { assets?: Record<string, { href?: string; 'eo:gsd'?: number }> }[];
    };
    const features = json.features ?? [];
    if (features.length === 0) throw new NoCoverageError('swisstopo');

    const hrefs: string[] = [];
    for (const f of features) {
      for (const asset of Object.values(f.assets ?? {})) {
        const href = asset.href;
        if (!href || !href.endsWith('.tif')) continue;
        // Prefer the 2 m product; the filename encodes resolution and CRS.
        if (asset['eo:gsd'] === 2 || /_2_2056_/.test(href)) {
          hrefs.push(href);
          break;
        }
      }
    }
    if (hrefs.length === 0) throw new NoCoverageError('swisstopo (no 2 m asset)');
    if (hrefs.length > MAX_TILES) throw new NoCoverageError('swisstopo (too many tiles)');

    const tiles = (
      await Promise.all(hrefs.map((h) => readTile(h, req.signal).catch(() => null)))
    ).filter((t): t is Tile => t !== null);

    if (tiles.length === 0) throw new NoCoverageError('swisstopo');

    const { gridSize } = domain;
    const data = new Float32Array(gridSize * gridSize);
    let filled = 0;

    for (let j = 0; j < gridSize; j++) {
      for (let i = 0; i < gridSize; i++) {
        const [lon, lat] = gridToLonLat(domain, i, j);
        const [E, N] = wgs84ToLv95(lon, lat);
        const v = sampleTiles(tiles, E, N);
        if (v !== null) {
          data[j * gridSize + i] = v;
          filled++;
        } else {
          data[j * gridSize + i] = NaN;
        }
      }
    }

    // Partial coverage (a domain straddling the border) is worse than falling
    // through to a global source that covers all of it.
    if (filled < gridSize * gridSize * 0.985) {
      throw new NoCoverageError('swisstopo (partial coverage)');
    }
    for (let i = 0; i < data.length; i++) if (Number.isNaN(data[i])) data[i] = 0;

    return {
      data,
      width: gridSize,
      height: gridSize,
      cellSize: domain.cellSize,
      origin: domain.origin,
      crs: domain.frame.crs,
      source: 'swissALTI3D 2 m',
    };
  },
};

async function readTile(href: string, signal?: AbortSignal): Promise<Tile> {
  const tiff: GeoTIFF = await fromUrl(href, { signal } as never);
  const image = await tiff.getImage();
  const bbox = image.getBoundingBox() as [number, number, number, number];

  // Downsample on read: geotiff.js will pick the matching overview level in a
  // COG, so this is cheaper than reading full resolution and averaging here.
  const width = Math.min(image.getWidth(), 256);
  const height = Math.min(image.getHeight(), 256);
  const rasters = await image.readRasters({ width, height, interleave: true });

  return {
    raster: Float32Array.from(rasters as unknown as ArrayLike<number>),
    width,
    height,
    bbox,
  };
}

/** Nearest tile containing (E, N), bilinearly sampled. */
function sampleTiles(tiles: Tile[], E: number, N: number): number | null {
  for (const t of tiles) {
    const [minE, minN, maxE, maxN] = t.bbox;
    if (E < minE || E > maxE || N < minN || N > maxN) continue;
    const fx = ((E - minE) / (maxE - minE)) * t.width - 0.5;
    // Raster row 0 is the north edge.
    const fy = ((maxN - N) / (maxN - minN)) * t.height - 0.5;

    const x0 = Math.min(t.width - 1, Math.max(0, Math.floor(fx)));
    const y0 = Math.min(t.height - 1, Math.max(0, Math.floor(fy)));
    const x1 = Math.min(t.width - 1, x0 + 1);
    const y1 = Math.min(t.height - 1, y0 + 1);
    const tx = Math.min(1, Math.max(0, fx - x0));
    const ty = Math.min(1, Math.max(0, fy - y0));

    const v =
      t.raster[y0 * t.width + x0] * (1 - tx) * (1 - ty) +
      t.raster[y0 * t.width + x1] * tx * (1 - ty) +
      t.raster[y1 * t.width + x0] * (1 - tx) * ty +
      t.raster[y1 * t.width + x1] * tx * ty;
    if (Number.isFinite(v) && v > -1000 && v < 5000) return v;
  }
  return null;
}
